/* Fit Profile — size finder, disclosures, language switch.
   Open/close/focus-trap/ESC/scroll-lock of the drawer come from
   <overlay-drawer>; the inline accordion comes from theme.collapsibles. */
(function () {
  if (customElements.get('fit-profile-finder')) return;

  var reducedMotion = function () {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  };

  // GTM is on the page; push to its dataLayer and nothing else. If GTM is
  // ever removed this becomes a no-op rather than creating a dataLayer.
  function track(name, data) {
    if (!Array.isArray(window.dataLayer)) return;
    var payload = { event: name };
    for (var k in data) if (data[k] !== undefined) payload['fp_' + k] = data[k];
    window.dataLayer.push(payload);
  }

  function fill(str, vals) {
    return (str || '')
      .replace(/\[size\]/g, vals.size == null ? '' : vals.size)
      .replace(/\[cm\]/g, vals.cm == null ? '' : vals.cm);
  }

  // Arabic-Indic / Persian digits, Arabic decimal separator and comma -> "24.5"
  function normalise(raw) {
    return String(raw || '')
      .replace(/[\u0660-\u0669]/g, function (d) { return d.charCodeAt(0) - 0x0660; })
      .replace(/[\u06F0-\u06F9]/g, function (d) { return d.charCodeAt(0) - 0x06f0; })
      .replace(/[\u066B,]/g, '.')
      .replace(/\s+/g, '')
      .replace(/(cm|\u0633\u0645)$/i, '');
  }

  /* ---------- product data shared by both language bodies ---------- */
  function scopeData(scope) {
    if (!scope) return null;
    if (scope._fpData) return scope._fpData;
    var data = { sizeIndex: -1, variants: [], current: null };
    var node = scope.querySelector('script[data-fp-variants]');
    try { if (node) data = Object.assign(data, JSON.parse(node.textContent)); } catch (e) {}
    data.byId = {};
    data.variants.forEach(function (v) { data.byId[v.id] = v; });
    scope._fpData = data;
    return data;
  }

  // The page's current variant. The theme's select[name=id] cannot be trusted
  // for this: sold-out <option>s have no value, so selecting one leaves the
  // select on the previous id. variant:change (below) keeps this up to date.
  function currentVariant(data) {
    if (data.current && data.byId[data.current]) return data.byId[data.current];
    var selects = document.querySelectorAll('select[name="id"]');
    for (var i = 0; i < selects.length; i++) {
      var v = data.byId[selects[i].value];
      if (v) { data.current = v.id; return v; }
    }
    return null;
  }

  class FitProfileFinder extends HTMLElement {
    connectedCallback() {
      // overlay-drawer moves itself to <body>, which reconnects this element.
      if (this._fp) return;
      this._fp = true;

      this.rows = (this.dataset.rows || '')
        .split(',')
        .map(function (p) {
          var b = p.split(':');
          return { eu: (b[0] || '').trim(), cm: parseFloat(b[1]) };
        })
        .filter(function (r) { return r.eu && !isNaN(r.cm); })
        .sort(function (a, b) { return a.cm - b.cm; });

      this.ease = parseFloat(this.dataset.ease) || 0;
      // The chart and measure guide are siblings of this element, not
      // children, so lookups go through the language body.
      this.root = this.closest('[data-fit-profile]') || document;
      this.scope = this.closest('[data-fp-scope]');
      this.rec = null;

      this.input = this.querySelector('[data-fp-input]');
      this.error = this.querySelector('[data-fp-error]');
      this.whatsapp = this.querySelector('[data-fp-whatsapp]');
      this.result = this.querySelector('[data-fp-result]');
      this.size = this.querySelector('[data-fp-size]');
      this.echo = this.querySelector('[data-fp-echo]');
      this.selectBtn = this.querySelector('[data-fp-action="add"]');
      this.addError = this.querySelector('[data-fp-add-error]');
      this.selected = this.querySelector('[data-fp-selected]');
      this.selectedText = this.querySelector('[data-fp-selected-text]');
      this.unavailable = this.querySelector('[data-fp-unavailable]');
      this.notifyBtn = this.querySelector('[data-fp-action="notify"]');
      if (this.selected) this.selected.setAttribute('tabindex', '-1');

      this.addEventListener('click', this.onClick.bind(this));
      this.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' && e.target === this.input) {
          e.preventDefault();
          this.calculate();
        }
      }.bind(this));

      syncAccordionHeight(this);
    }

    onClick(e) {
      var btn = e.target.closest('[data-fp-action]');
      if (!btn || !this.contains(btn)) return;
      switch (btn.dataset.fpAction) {
        case 'calculate': this.calculate(); break;
        case 'add': this.add(); break;
        case 'notify': this.notify(btn); break;
        case 'measure': openMeasure(this.root, 'link'); break;
      }
    }

    context(extra) {
      var out = {
        product_id: this.scope && this.scope.dataset.productId,
        lang: this.dataset.lang
      };
      for (var k in extra) out[k] = extra[k];
      return out;
    }

    clear() {
      if (this.error) this.error.textContent = '';
      if (this.input) this.input.removeAttribute('aria-invalid');
      if (this.whatsapp) this.whatsapp.hidden = true;
      if (this.result) this.result.hidden = true;
      this.rec = null;
      this.root.querySelectorAll('[data-fp-row].is-match').forEach(function (el) {
        el.classList.remove('is-match');
      });
    }

    fail(message, offerHelp) {
      if (this.error) this.error.textContent = message || '';
      if (this.input) this.input.setAttribute('aria-invalid', 'true');
      if (offerHelp && this.whatsapp) this.whatsapp.hidden = false;
      fitAccordion(this.closest('.collapsible-content'));
    }

    // The sizing rule, unchanged: foot + the last's ease, smallest size whose
    // chart length is >= that. Returns { row } or { error }.
    //
    // There is no between-sizes rule. When the factory approves one, return
    // { row, explain: '...' } from here - render() already shows `explain`.
    recommend(foot) {
      var target = foot + this.ease;
      if (target < this.rows[0].cm) return { error: 'low' };
      if (target > this.rows[this.rows.length - 1].cm) return { error: 'high' };
      for (var i = 0; i < this.rows.length; i++) {
        if (this.rows[i].cm >= target) return { row: this.rows[i] };
      }
      return { error: 'high' };
    }

    calculate() {
      this.clear();
      if (!this.rows.length) return;

      var raw = normalise(this.input && this.input.value);
      track('find_my_size_click', this.context({}));

      if (!raw) return this.fail(this.dataset.msgEmpty, false);
      var value = /^\d+(\.\d+)?$/.test(raw) ? parseFloat(raw) : NaN;
      if (isNaN(value) || value < 15 || value > 35) {
        return this.fail(this.dataset.msgInvalid, false);
      }

      var rec = this.recommend(value);
      if (rec.error === 'low') return this.fail(this.dataset.msgLow, true);
      if (rec.error) return this.fail(this.dataset.msgHigh, true);

      this.rec = { eu: rec.row.eu, cm: value, explain: rec.explain || '' };
      this.addedId = null;

      if (this.size) this.size.textContent = fill(this.dataset.msgSize, { size: rec.row.eu });
      if (this.echo) this.echo.textContent = fill(this.dataset.msgNote, { cm: value });
      var explain = this.querySelector('[data-fp-explain]');
      if (explain) {
        explain.textContent = this.rec.explain;
        explain.hidden = !this.rec.explain;
      }

      var row = this.root.querySelector('[data-fp-row="' + CSS.escape(rec.row.eu) + '"]');
      if (row) row.classList.add('is-match');

      var state = this.render();
      if (this.result) this.result.hidden = false;

      fitAccordion(this.closest('.collapsible-content'));
      track('size_recommendation_shown', this.context({
        size: rec.row.eu,
        foot_cm: value,
        available: state !== 'unavailable'
      }));
    }

    // The variant she would get: her current colour/other options with the
    // recommended size swapped in. Never touches any other option.
    target(data) {
      if (!this.rec || !data || data.sizeIndex < 0) return null;
      var idx = data.sizeIndex;
      var eu = this.rec.eu;
      var cur = currentVariant(data);
      return data.variants.filter(function (v) {
        if (v.options[idx] !== eu) return false;
        if (!cur) return true;
        return v.options.every(function (o, i) { return i === idx || o === cur.options[i]; });
      })[0] || null;
    }

    // Decides which action the result card offers. Called on calculate and
    // again on every variant change, so it never goes stale.
    render() {
      if (!this.rec) return null;
      var data = scopeData(this.scope);
      var v = this.target(data);
      var cur = data && currentVariant(data);
      var state;

      if (!data || data.sizeIndex < 0) state = 'none';
      else if (!v || !v.available) state = 'unavailable';
      else if (this.addedId === v.id && cur && cur.id === v.id) state = 'added';
      else state = 'add';

      var size = { size: this.rec.eu };
      if (this.selectBtn) {
        this.selectBtn.textContent = fill(this.dataset.msgAdd, size);
        this.selectBtn.hidden = state !== 'add';
      }
      if (this.addError && state !== 'add') this.addError.hidden = true;
      if (this.selectedText) this.selectedText.textContent = fill(this.dataset.msgAdded, size);
      if (this.selected) this.selected.hidden = state !== 'added';
      if (this.unavailable) this.unavailable.hidden = state !== 'unavailable';
      if (this.notifyBtn) {
        this.notifyBtn.textContent = fill(this.dataset.msgNotify, size);
        this.notifyBtn.hidden = !(state === 'unavailable' && v && document.getElementById('notifyModal'));
      }
      fitAccordion(this.closest('.collapsible-content'));
      return state;
    }

    setBusy(on) {
      this.busy = on;
      if (!this.selectBtn) return;
      this.selectBtn.disabled = on;
      this.selectBtn.setAttribute('aria-busy', on ? 'true' : 'false');
    }

    // One tap: select the recommended size on the page, then press the
    // theme's own Add to cart - so the cart drawer, cart count and any app
    // listening to the product form behave exactly as a normal add.
    add() {
      var data = scopeData(this.scope);
      var v = this.target(data);
      if (this.busy) return;
      if (!v || !v.available) { this.render(); return; }
      if (this.addError) this.addError.hidden = true;
      this.setBusy(true);

      var self = this;
      var done = function (ok) {
        self.setBusy(false);
        if (ok) {
          self.addedId = v.id;
          track('recommended_size_add_to_cart', self.context({ size: self.rec && self.rec.eu, variant_id: v.id }));
        } else if (self.addError) {
          self.addError.textContent = self.dataset.msgAddError || '';
          self.addError.hidden = false;
        }
        self.render();
        if (ok && self.selected) self.selected.focus({ preventScroll: true });
      };

      this.select(data, v);
      this.whenSelected(data, v, function (selected) {
        if (!selected) return done(false);
        self.pressAddToCart(done);
      });
    }

    // Wait for the theme to confirm the variant (variant:change updates
    // data.current). Never press Add to cart on an unconfirmed size.
    whenSelected(data, v, cb) {
      var start = Date.now();
      (function check() {
        var cur = currentVariant(data);
        if (cur && cur.id === v.id) return cb(true);
        if (Date.now() - start > 2000) return cb(false);
        setTimeout(check, 50);
      })();
    }

    pressAddToCart(done) {
      var section = (this.scope && this.scope.closest('.product-section')) || document;
      var btn = section.querySelector('form[action*="/cart/add"] [data-add-to-cart]');
      var form = btn && (btn.form || btn.closest('form'));
      if (!btn || !form || btn.disabled) return done(false);

      var settled = false;
      var finish = function (ok) {
        if (settled) return;
        settled = true;
        form.removeEventListener('ajaxProduct:added', onAdded);
        form.removeEventListener('ajaxProduct:error', onError);
        done(ok);
      };
      // On a 422 (e.g. not enough stock) the theme fires ajaxProduct:error
      // and THEN ajaxProduct:added, so the first event decides.
      var onAdded = function () { finish(true); };
      var onError = function () { finish(false); };
      form.addEventListener('ajaxProduct:added', onAdded);
      form.addEventListener('ajaxProduct:error', onError);
      // Guards a silent failure only; if the theme is set to go to the cart
      // page instead, the page unloads first.
      setTimeout(function () { finish(false); }, 8000);
      btn.click();
    }

    // Drive the swatch app's own button when it is there: it hides the
    // theme's radios, so ticking a radio directly changes the form while the
    // visible swatch still shows the old size. Fall back to the radio.
    select(data, v) {
      var cur = currentVariant(data);
      if (cur && cur.id === v.id) return;

      var eu = this.rec.eu;
      var option = 'option' + (data.sizeIndex + 1);
      var root = (this.scope && this.scope.closest('.product-section')) || document;

      var swatch = [].slice
        .call(root.querySelectorAll(
          '.swatch-button[swatch-option="' + option + '"][data-value="' + CSS.escape(eu) + '"]'))
        .filter(function (b) { return b.getBoundingClientRect().width > 0; })[0];

      if (swatch) {
        swatch.click();
      } else {
        var input = root.querySelector(
          'fieldset[data-index="' + option + '"] .variant-input[data-value="' +
          CSS.escape(eu) + '"] input[type="radio"]');
        if (input && !input.checked) {
          input.checked = true;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }

      track('recommended_size_selected', this.context({ size: eu, variant_id: v.id }));
    }

    // notify-me.js opens its modal for a click on any .notify-me-btn, using
    // that button's data-* attributes. Borrow the class for this one click
    // (this listener runs before notify-me's document listener) and hand it
    // the recommended variant, without selecting a sold-out size on the page.
    notify(btn) {
      var data = scopeData(this.scope);
      var v = this.target(data);
      if (!v || !this.scope) return;
      btn.dataset.variantId = v.id;
      btn.dataset.variantTitle = v.title;
      btn.dataset.notifySku = v.sku || '';
      btn.dataset.productId = this.scope.dataset.productId;
      btn.dataset.productHandle = this.scope.dataset.productHandle;
      btn.dataset.productTitle = this.scope.dataset.productTitle;
      btn.classList.add('notify-me-btn');
      setTimeout(function () { btn.classList.remove('notify-me-btn'); }, 0);
    }
  }

  customElements.define('fit-profile-finder', FitProfileFinder);

  /* ---------- disclosures: How to measure / View size chart ---------- */
  function setOpen(toggle, open) {
    var panel = document.getElementById(toggle.getAttribute('aria-controls'));
    var body = toggle.closest('[data-fit-profile]');
    if (body) panel = body.querySelector('#' + CSS.escape(toggle.getAttribute('aria-controls'))) || panel;
    if (!panel) return;
    toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    panel.hidden = !open;
    // Update the accordion now rather than waiting for ResizeObserver, so a
    // scroll that follows lands on the final layout.
    fitAccordion(toggle.closest('.collapsible-content'));
  }

  function openMeasure(body, source) {
    var toggle = body.querySelector('[data-fp-section="measure"] [data-fp-toggle]');
    if (!toggle) return;
    var wasOpen = toggle.getAttribute('aria-expanded') === 'true';
    setOpen(toggle, true);
    toggle.focus({ preventScroll: true });
    // No frame wait needed: getBoundingClientRect forces layout, and the
    // toggle sits above the panel that is growing.
    scrollToEl(toggle);
    if (!wasOpen) track('how_to_measure_open', { source: source, lang: body.dataset.fpLang });
  }

  // On the page, scroll the window by hand: scrollIntoView would also scroll
  // the accordion itself (overflow:hidden, still scrollable by script) and
  // leave its content shifted. Offset by the fixed/sticky header.
  function scrollToEl(el) {
    var behavior = reducedMotion() ? 'auto' : 'smooth';
    if (el.closest('overlay-drawer')) {
      el.scrollIntoView({ behavior: behavior, block: 'start' });
      return;
    }
    var offset = 16;
    var header = document.querySelector('.site-header');
    if (header) {
      var pos = getComputedStyle(header).position;
      if (pos === 'fixed' || pos === 'sticky') offset += Math.max(0, header.getBoundingClientRect().bottom);
    }
    var top = el.getBoundingClientRect().top + window.pageYOffset - offset;
    window.scrollTo({ top: Math.max(0, top), behavior: behavior });
  }

  /* The theme accordion sets a fixed pixel height when it opens. Anything
     that grows inside it later (result card, a disclosure, the language
     switch) would be clipped, so keep that height in step with the content. */
  function fitAccordion(content) {
    if (!content) return;
    var inner = content.querySelector('.collapsible-content__inner');
    var h = content.style.height;
    if (!inner || !content.classList.contains('is-open') || !h || h === 'auto' || h === '0px') return;
    content.style.height = inner.offsetHeight + 'px';
  }

  function syncAccordionHeight(el) {
    var content = el.closest('.collapsible-content');
    if (!content || content._fpObserved || !window.ResizeObserver) return;
    content._fpObserved = true;
    var inner = content.querySelector('.collapsible-content__inner');
    if (!inner) return;
    new ResizeObserver(function () { fitAccordion(content); }).observe(inner);
  }

  document.addEventListener('click', function (e) {
    var toggle = e.target.closest('[data-fp-toggle]');
    if (toggle) {
      var body = toggle.closest('[data-fit-profile]');
      var section = toggle.closest('[data-fp-section]');
      if (section && section.dataset.fpSection === 'measure' &&
          toggle.getAttribute('aria-expanded') !== 'true') {
        openMeasure(body, 'toggle');
        return;
      }
      setOpen(toggle, toggle.getAttribute('aria-expanded') !== 'true');
      return;
    }

    // size_guide_open: the theme's own handlers run on the element first, so
    // by the time this document listener runs the new state is already set.
    var trigger = e.target.closest('.fit-profile-inline .collapsible-trigger, [data-open-drawer^="fit-profile-drawer"]');
    if (!trigger) return;
    setTimeout(function () {
      var inline = trigger.classList.contains('collapsible-trigger');
      if (inline && trigger.getAttribute('aria-expanded') !== 'true') return;
      var host = inline ? trigger.parentElement : document.getElementById(trigger.dataset.openDrawer);
      var scope = host && host.querySelector('[data-fp-scope]');
      var visible = host && host.querySelector('[data-fit-profile]:not([hidden])');
      track('size_guide_open', {
        product_id: scope && scope.dataset.productId,
        lang: visible && visible.dataset.fpLang,
        layout: inline ? 'inline' : 'drawer'
      });
    }, 0);
  });

  // Keep every open recommendation honest when the shopper changes colour or
  // size on the page herself.
  function onVariantChange(e) {
    var variant = e.detail && e.detail.variant;
    document.querySelectorAll('[data-fp-scope]').forEach(function (scope) {
      var data = scopeData(scope);
      if (variant && data.byId[variant.id]) data.current = variant.id;
      scope.querySelectorAll('fit-profile-finder').forEach(function (f) {
        if (f.rec) f.render();
      });
    });
  }
  document.addEventListener('variant:change', onVariantChange);

  /* Language switch: flips the guide between the two rendered bodies.
   Both languages are already in the HTML - this only toggles [hidden]. */
  var LANG_KEY = 'fitProfileLang';

  class FitProfileLang extends HTMLElement {
    connectedCallback() {
      if (this._fp) return;
      this._fp = true;
      this.buttons = [].slice.call(this.querySelectorAll('[data-fp-lang-btn]'));
      this.addEventListener('click', function (e) {
        var b = e.target.closest('[data-fp-lang-btn]');
        if (b) this.select(b.dataset.fpLangBtn);
      }.bind(this));

      var saved = null;
      try { saved = localStorage.getItem(LANG_KEY); } catch (e) {}
      if (saved) this.select(saved);
    }

    select(lang) {
      var bodies = [].slice.call(
        (this.parentElement || document).querySelectorAll('[data-fp-lang]'));
      if (!bodies.some(function (b) { return b.dataset.fpLang === lang; })) return;

      bodies.forEach(function (b) { b.hidden = b.dataset.fpLang !== lang; });
      this.buttons.forEach(function (b) {
        var on = b.dataset.fpLangBtn === lang;
        b.classList.toggle('is-on', on);
        b.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
      try { localStorage.setItem(LANG_KEY, lang); } catch (e) {}
      fitAccordion(this.closest('.collapsible-content'));
    }
  }

  if (!customElements.get('fit-profile-lang')) customElements.define('fit-profile-lang', FitProfileLang);
})();
